import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Wallet } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { canAccessAdminSettings } from "@/lib/settingsAccess";

// =====================================================================
// Settings → Methods Accepted
//
// Toggle the payment methods the front desk can record at checkout.
// Saves to clinic_settings.extras.digital_payment_methods (text[]).
//
// 'cash' is always implicitly accepted and not part of the toggle list
// — it's the universal fallback. Toggles are for digital methods only.
// =====================================================================

const METHODS = [
  { key: "gcash",         label: "GCash",          desc: "Mobile wallet — most popular in PH." },
  { key: "maya",          label: "Maya (PayMaya)", desc: "Mobile wallet." },
  { key: "bank_transfer", label: "Bank transfer",  desc: "InstaPay / PESONet." },
  { key: "credit_card",   label: "Credit card",    desc: "Card payments at the front desk." },
  { key: "debit_card",    label: "Debit card",     desc: "Card payments at the front desk." },
  { key: "hmo",           label: "HMO",            desc: "Insurance / health-plan claim." },
  { key: "check",         label: "Check",          desc: "Old-school but still common for B2B." },
];

export default function SettingsMethods() {
  const { clinicRole, isPlatformAdmin } = useAuth();
  const canManageMethods = canAccessAdminSettings(clinicRole, isPlatformAdmin);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [accepted, setAccepted] = useState(new Set());

  const load = useCallback(async () => {
    if (!canManageMethods) {
      setLoading(false);
      setError(null);
      setAccepted(new Set());
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("get_clinic_settings");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load."); return; }
    const arr = Array.isArray(data?.digital_payment_methods) ? data.digital_payment_methods : [];
    setAccepted(new Set(arr));
  }, [canManageMethods]);

  useEffect(() => { load(); }, [load]);

  function toggle(key) {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true); setError(null);
    const { error: e } = await supabase.rpc("update_payment_methods", { p_methods: [...accepted] });
    setSaving(false);
    if (e) { setError(e.message ?? "Save failed."); return; }
    toast.success("Payment methods saved.");
  }

  if (!canManageMethods || (error && /permission denied/i.test(error))) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="methods-denied">
        <Wallet className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only a Clinic Admin can manage payment methods.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-methods">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <h1 className="text-lg font-semibold text-slate-900">Methods accepted</h1>
        <p className="text-xs text-slate-500">Tick the digital payment methods your front desk can record at checkout. Cash is always available — no toggle needed.</p>
      </div>

      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
          {METHODS.map((m) => (
            <label key={m.key} className="flex items-start gap-4 px-5 py-3 cursor-pointer">
              <input type="checkbox" checked={accepted.has(m.key)} onChange={() => toggle(m.key)}
                data-testid={`method-${m.key}`}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{m.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={saving || loading}
          data-testid="methods-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}
